/**
 * Centralized route path constants, mirroring the tree defined in `src/app/routes/router.tsx`.
 * Keep this file in sync when routes are added/renamed there — it is the single source of
 * truth other layers should import from instead of hardcoding path strings.
 *
 * Only absolute paths worth reusing outside the router are listed here. Route segments that
 * only make sense as *relative* navigation (e.g. `navigate('patients')` from within an
 * `organizations/:id` layout route) are intentionally left as-is; turning those into absolute
 * constants would change their behavior.
 */

export const ROUTES = {
  ROOT: '/',
  HOME: '/home',
  LOGIN: '/login',
  SWITCH_COMPANY: '/switch-company',
  TWO_FACTOR_LOGIN: '/twofactor-login',
  AGREEMENT: '/agreement',
  FORGOT_PASSWORD: '/forgot-password',
  NOTIFICATIONS: '/notifications',
  GLOBAL_LOOKUP: '/global-lookup',

  ERROR: {
    FORBIDDEN: '/error/forbidden',
    NOT_FOUND: '/error/not-found'
  },

  QUICK_UPLOAD: {
    ROOT: '/quick-upload',
    THANKS: '/quick-upload/thanks',
    session: (sessionId: string) => `/quick-upload/${sessionId}`,
    medications: (sessionId: string) => `/quick-upload/${sessionId}/medications`,
    insurance: (sessionId: string) => `/quick-upload/${sessionId}/insurance`,
    combinePdf: (sessionId: string) => `/quick-upload/${sessionId}/combine-pdf`
  },

  PROVIDERS: {
    ROOT: '/providers',
    detail: (id: string) => `/providers/${id}`
  },

  PATIENTS: {
    ROOT: '/patients',
    TAGGED: '/patients/tagged',
    LIST: '/patients/list',
    IMPORTED_LIST: '/patients/imported-list',
    INSIGHTS: '/patients/insights',
    CONVERSATIONS: '/patients/conversations',
    MED_HISTORY_REQUESTS: '/patients/med-history-requests',
    PATIENTS_TO_FOLLOWUP: '/patients/patients-to-followup',
    LOOKUP: '/patients/lookup',
    detail: (id: string) => `/patients/${id}/details`,
    triage: (id: string) => `/patients/${id}/details/medications/triage`,
    /** `taskId` opens that task in the list. */
    tasksList: (id: string, taskId?: string) => `/patients/${id}/details/medications/tasks/list${taskId ? `?taskId=${taskId}` : ''}`,
    rpmReports: (id: string) => `/patients/${id}/details/rpm/reports`
  },

  TASKS: {
    ROOT: '/tasks'
  },

  TRANSITIONAL_CARE_MANAGEMENT: {
    ROOT: '/transitional-care-management',
    DASHBOARD: '/transitional-care-management/dashboard',
    ADT_QUEUE: '/transitional-care-management/adt-queue',
    ADT_QUEUE_V1: '/transitional-care-management/adt-queue-v1',
    BILLING: '/transitional-care-management/billing',
    TCM_TASKS: '/transitional-care-management/tcm-tasks',
    DISCHARGE: '/transitional-care-management/discharge'
  },

  REMOTE_PATIENT_MONITORING: {
    ROOT: '/remote-patient-monitoring',
    MY_PATIENTS: '/remote-patient-monitoring/my-patients',
    PROVIDER_APPROVALS: '/remote-patient-monitoring/provider-approvals',
    CLAIMS: '/remote-patient-monitoring/claims',
    ALERTS: '/remote-patient-monitoring/alerts',
    DEVICES: '/remote-patient-monitoring/devices',
    REPORTS_COMMUNICATION: '/remote-patient-monitoring/reports-communication',
    PATIENT_SCHEDULES: '/remote-patient-monitoring/patient-schedules',
    FOLLOW_UP_TASKS: '/remote-patient-monitoring/follow-up-tasks',
    REVIEW_PATIENTS: '/remote-patient-monitoring/review-patients'
  },

  CHRONIC_CARE_MANAGEMENT: {
    ROOT: '/chronic-care-management',
    PATIENTS: '/chronic-care-management/patients'
  },

  CARE_MANAGEMENT: {
    ROOT: '/care-management',
    PATIENTS: '/care-management/patients',
    DASHBOARD: '/care-management/dashboard',
    WORKQUEUE: '/care-management/workqueue'
  },

  REQUESTS: {
    ROOT: '/requests'
  },

  REPORTS: {
    ROOT: '/reports',
    MY_DASHBOARD: '/reports/my-dashboard',
    DASHBOARD: '/reports/dashboard',
    ENCOUNTERS: '/reports/encounters',
    BILLING: '/reports/billing',
    TASKS: '/reports/tasks',
    HEALTH_PLAN: '/reports/health-plan',
    PERFORMANCE: '/reports/performance',
    CLAIMS: '/reports/claims',
    RPM_PROVIDERS: '/reports/rpm-providers',
    RPM_COMPANIES: '/reports/rpm-companies',
    RPM_CHART_REVIEWS: '/reports/rpm-chart-reviews',
    WORKQUEUE: '/reports/workqueue',
    RPM_PROVIDER_SIGNOFFS: '/reports/rpm-provider-signoffs',
    SESSION_TIME: '/reports/session-time',
    RPM_METRICS: '/reports/rpm-metrics',
    CCM_REPORTS: '/reports/ccm-reports',
    PAYER_CHART_REQUESTS: '/reports/payer-chart-requests',
    AI_SEARCH_FEEDBACK: '/reports/ai-search-feedback'
  },

  ADMINISTRATION: {
    ROOT: '/administration',
    TIMESHEETS: '/administration/timesheets',
    INVOICES: '/administration/invoices',
    DASHBOARD: '/administration/dashboard',
    ORGANIZATIONS: '/administration/organizations',
    USERS: '/administration/users',
    PATIENTS: '/administration/patients',
    HEALTH_PLANS: '/administration/health-plans',
    RESOURCES: '/administration/resources',
    PROVIDERS: {
      ROOT: '/administration/providers',
      DIRECTORY: '/administration/providers/directory',
      AVAILABILITY: '/administration/providers/availability'
    },
    LOGS: '/administration/logs',
    MY_PERFORMANCE: '/administration/my-performance',
    ANNOUNCEMENTS: '/administration/announcements',
    RX_EVENT_METRICS: '/administration/rx-event-metrics',
    MOCK_APIS: '/administration/mock-apis',
    SYSTEM: {
      ROOT: '/administration/system',
      CONFIGURATIONS: '/administration/system/configurations',
      COMPANIES: '/administration/system/companies',
      DATA_IMPORT: '/administration/system/data-import',
      ELIGIBILITY_CHECKS: '/administration/system/eligibility-checks'
    },
    invoiceDetail: (id: string) => `/administration/invoices/${id}`,
    organizationDetail: (id: string) => `/administration/organizations/${id}`,
    organizationStoreDetail: (orgId: string, storeId: string) => `/administration/organizations/${orgId}/stores/${storeId}`,
    userDetail: (id: string) => `/administration/users/${id}/details`,
    companyDetail: (rpid: string) => `/administration/system/companies/${rpid}`,
    dataImportRunDetail: (runId: string) => `/administration/system/data-import/runs/${runId}`,
    rakeRunDetail: (runId: string) => `/administration/system/data-import/rake-runs/${runId}`
  }
} as const;
